// Sharing platform configuration and utilities
import { DailyState, PersistentState } from '../types/GameTypes';
import { formatDateForResults } from '../utils/dateUtils';

export interface SharingPlatform {
  name: string;
  icon: string;
  title: string;
  generateUrl: (message?: string) => string;
  generateMessage: (dailyState: DailyState, persistentState: PersistentState) => string;
  characterLimit?: number;
}

// Utility functions

export const generateWordResults = (results: any[]): string => {
  return results.map(result => {
    const status = result.success ? `✓ (${result.score})` : '✗';
    return `${result.length}-letter word: ${status}`;
  }).join('\n');
};

export const generateStreakInfo = (persistentState: PersistentState, includeShortStreaks: boolean = true): string => {
  const minStreak = includeShortStreaks ? 1 : 2;
  if (persistentState.streak >= minStreak) {
    const dayText = persistentState.streak === 1 ? 'day' : 'days';
    return `Current Streak: ${persistentState.streak} ${dayText} (${persistentState.streakScore} total points)`;
  }
  return '';
};

// Platform configurations
export const SHARING_PLATFORMS: Record<string, SharingPlatform> = {
  clipboard: {
    name: 'clipboard',
    icon: 'clipboard',
    title: 'Copy Results',
    generateUrl: () => '', // No URL needed for clipboard
    generateMessage: (dailyState: DailyState, persistentState: PersistentState) => {
      const totalScore = `RSTLNE Daily Challenge (${formatDateForResults(dailyState.date)}): ${dailyState.totalScore} points`;
      const wordsResult = generateWordResults(dailyState.results);
      const streakInfo = generateStreakInfo(persistentState, true); // Include short streaks for clipboard
      
      return streakInfo ? 
        `${totalScore}\n\n${wordsResult}\n\n${streakInfo}` : 
        `${totalScore}\n\n${wordsResult}`;
    }
  },

  twitter: {
    name: 'twitter',
    icon: 'twitter',
    title: 'Tweet',
    characterLimit: 280,
    generateUrl: (message: string = '') => 
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`,
    generateMessage: (dailyState: DailyState, persistentState: PersistentState) => {
      const successCount = dailyState.results.filter(result => result.success).length;
      const totalWords = dailyState.results.length;
      const wordResults = `${successCount}/${totalWords} words`;
      const streakText = persistentState.streak > 1 ? ` | ${persistentState.streak} day streak` : '';
      
      return `RSTLNE Daily: ${dailyState.totalScore} pts (${wordResults})${streakText}\n\nPlay at rstlne.app #RSTLNE #WordGame`;
    }
  },

  facebook: {
    name: 'facebook',
    icon: 'facebook',
    title: 'Share on Facebook',
    characterLimit: 63206,
    // Facebook's sharer API has limitations: custom text in 'quote' parameter is often ignored
    // Modern approach: use simple sharer with URL only - users can add their own text
    // Alternative: Use Facebook SDK's share dialog for better message control (requires more setup)
    generateUrl: () => 
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://rstlne.app')}`,
    generateMessage: (dailyState: DailyState, persistentState: PersistentState) => {
      // Message for Facebook - no need to include link since Facebook embeds it automatically
      // Users can copy this and paste it manually when Facebook dialog opens
      const totalScore = `RSTLNE Daily Challenge (${formatDateForResults(dailyState.date)}): ${dailyState.totalScore} points`;
      const wordsResult = generateWordResults(dailyState.results);
      const streakInfo = generateStreakInfo(persistentState, false);
      
      return streakInfo ? 
        `${totalScore}\n\n${wordsResult}\n\n${streakInfo}` :
        `${totalScore}\n\n${wordsResult}`;
    }
  },
  
  whatsapp: {
    name: 'whatsapp',
    icon: 'whatsapp', 
    title: 'Share via WhatsApp',
    generateUrl: (message: string = '') =>
      `https://wa.me/?text=${encodeURIComponent(message)}`,
    generateMessage: (dailyState: DailyState, persistentState: PersistentState) => {
      const totalScore = `🎯 RSTLNE Daily Challenge (${formatDateForResults(dailyState.date)}): ${dailyState.totalScore} points`;
      const wordsResult = generateWordResults(dailyState.results);
      const streakInfo = generateStreakInfo(persistentState, false); // Don't include short streaks for WhatsApp
      
      return streakInfo ? 
        `${totalScore}\n\n${wordsResult}\n\n${streakInfo}\n\nPlay at: rstlne.app` :
        `${totalScore}\n\n${wordsResult}\n\nPlay at: rstlne.app`;
    }
  }
};